import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import { getUploadLogs, setFilters, getUploadLogData, viewUploadLog } from '../store/uploads/actions';
import { capitalizeFirst, truncateString } from '../commonUtils/string.helper';
import ClickCopyText from '../components/ClickCopyText';
import { useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel } from '@tanstack/react-table';
import constants from '../commonUtils/constants';
import debounce from 'lodash/debounce';
import { ArrowPathIcon, ArrowTopRightOnSquareIcon, CheckCircleIcon, DocumentMagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/solid';
import IconButton from '../components/IconButton';
import { useNavigate } from 'react-router-dom';

const UploadHistoryContext = createContext();

// Statuses that trigger active polling
const POLLING_STATUSES = new Set(['Processing Submission', 'Processing Field Mapping']);
// Polling interval (e.g., 30 seconds)
const INTERVAL_MS = 30 * 1000; 

export const UploadHistoryProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const { user } = useSelector((state) => state.Auth);
    const { UploadLogData, filters } = useSelector((state) => state.Uploads);
    const [openDeleteConfirmation] = useState(false);
    // const [toDeleteId, setToDeleteId] = useState(null);
    const [viewUploadLogData, setViewUploadLogData] = useState(null);
    const [openViewLogModal, setOpenViewLogModal] = useState(false);
    
    const [action, setAction] = useState({id: "", open: false, modal: ""});
    const [rotatingRows, setRotatingRows] = useState(new Set());

    // ─── Polling Timers Management ──────────────────────────────────────────
    const pollingTimers = useRef({});

    const stopPolling = useCallback((key) => {
        const timers = pollingTimers.current[key];
        if (!timers) return;
        clearInterval(timers.intervalTimer);
        delete pollingTimers.current[key];
    }, []);

    const startPolling = useCallback((key) => {
        if (pollingTimers.current[key]) return; // Already polling

        pollingTimers.current[key] = { intervalTimer: null };

        const intervalTimer = setInterval(() => {
            const logs = pollingTimers.current[key]?.getLogs?.() || [];
            const log = logs.find(l => l.key === key);

            // Stop polling if log disappeared or status is no longer a polling status
            if (!log || !POLLING_STATUSES.has(log.status)) {
                stopPolling(key);
                return;
            }

            // Fetch the latest status for this specific upload
            dispatch(getUploadLogData({ key }));
        }, INTERVAL_MS);

        pollingTimers.current[key].intervalTimer = intervalTimer;
    }, [dispatch, stopPolling]);

    // ────────────────────────────────────────────────────────────────────────

    const columnHelper = useMemo(() => createColumnHelper(), []);
    
    const uploadLogs = useMemo(() => {
        return UploadLogData?.data?.map(({
            _id, key, file, master_project, project, source_type, vendor, channel_partner, spoc, lead_source, data_category, proc_reason, uploaded_by, is_uploader, extracted_on, formatted_created_at, field_mapping, etl_process, status, pending, error_message, remarks
        }, index) => {
            let uploadStatus;
            if (status === "review_rejected" || etl_process?.status === "review_rejected") {
                uploadStatus = 'Fields Rejected';
            } else if (pending && etl_process?.status === "pending") {
                uploadStatus = 'Pending';
            } else if (etl_process?.status === "failed") {
                uploadStatus = 'Process Failed';
            } else if (etl_process?.status === "completed") {
                uploadStatus = 'Completed';
            } else if (etl_process?.status === "exec_id_received" || etl_process?.status === "processing") {
                uploadStatus = 'Processing Submission';
            } else if (field_mapping?.status === "failed") {
                uploadStatus = 'Field Mapping Failed';
            } else if (field_mapping?.status === "completed") {
                uploadStatus = 'Fields Mapped';
            } else if (field_mapping?.status === "exec_id_received" || field_mapping?.status === "processing") {
                uploadStatus = 'Processing Field Mapping';
            } else {
                uploadStatus = status ? status.replace(/[_\s]+/g, ' ') : 'Error';
            }
    
            return {
                id: index + 1 + ((UploadLogData?.pagination?.currentPage - 1) * (UploadLogData?.pagination?.perPage || 0)),
                _id,
                key,
                fileName: file?.unique_name,
                fileOriginalName: file?.original_name,
                master_project:master_project?.title, 
                project: project?.title,
                source_type,
                channel_partner: channel_partner?.title || "",
                vendor: vendor?.title || "",
                spoc: spoc?.name,
                uploaded_by: uploaded_by?.name,
                data_category: data_category?.title,
                lead_source: lead_source?.title,
                proc_reason,
                extracted_on,
                uploaded_on: formatted_created_at,
                is_uploader,
                status: uploadStatus,
                pending,
                error_message,
                remarks
            };
        });
    }, [UploadLogData]);

    // Sync polling state with the live upload logs whenever they change
    useEffect(() => {
        const logs = uploadLogs || [];
        
        // Refresh live data reference for all active timers
        Object.keys(pollingTimers.current).forEach(key => {
            if (pollingTimers.current[key]) {
                pollingTimers.current[key].getLogs = () => logs;
            }
        });

        // Start/Stop polling based on current statuses
        logs.forEach(log => {
            if (POLLING_STATUSES.has(log.status)) {
                startPolling(log.key);
            } else {
                stopPolling(log.key);
            }
        });
    }, [uploadLogs, startPolling, stopPolling]);

    // CLEANUP: Stop all polling if the user navigates away (component unmounts)
    useEffect(() => {
        const currentTimers = pollingTimers.current;
        return () => {
            Object.keys(currentTimers).forEach(stopPolling);
        };
    }, [stopPolling]);

    const statusArray = ["Completed", "Process Failed", "Processing Submission", "Pending", "Fields Rejected"];
    const detailedViewStatusArray = ["Fields Mapped", "Completed", "Pending", "Process Failed", "Fields Rejected"];
    
    const columns = [
        columnHelper.accessor('id', {
            header: '#',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('key', {
            header: 'Key',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('fileName', {
            header: 'File Name',
            cell: (info) => (
                <ClickCopyText
                    displayText={truncateString(info.getValue(), 20, "....csv")}
                    copyText={info.getValue()}
                />
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            cell: (info) => (
                <>
                    {!statusArray.includes(info.row.original.status) && info.row.original.is_uploader && <IconButton
                        variant="action_default"
                        size="icon"
                        Icon={ArrowTopRightOnSquareIcon}
                        onClick={() => dispatch(getUploadLogData({
                            key: info.row.original.key,
                            navigate,
                            path: `/upload-files/${info.row.original.key}`
                        }))}
                    />}
                    <IconButton
                        variant="action_default"
                        size="icon"
                        Icon={EyeIcon}
                        onClick={() => handleShowViewLogModal(info.row.original)}
                        // onClick={() => navigate(`/upload-files/${info.row.original.key}`)}
                    />
                    {detailedViewStatusArray.includes(info.row.original.status) && <IconButton
                        variant="action_default"
                        size="icon"
                        Icon={DocumentMagnifyingGlassIcon}
                        onClick={() => dispatch(viewUploadLog({
                            key: info.row.original.key,
                            navigate,
                            path: `/view-data/${info.row.original.key}`
                        }))}
                        title="Full View"
                    />}
                    {info.row.original.status === "Processing Submission" && (
                        <IconButton
                            variant="action_default"
                            size="icon"
                            Icon={ArrowPathIcon}
                            onClick={() => handleRefreshClick(info.row.original.key)}
                            disabled={rotatingRows.has(info.row.original.key)}
                            className={getRefreshButtonClasses(info.row.original.key)}
                        />
                    )}
                    {(info.row.original.pending && user.role.key==="ADM") && <IconButton
                        variant="action_default"
                        size="icon"
                        Icon={CheckCircleIcon}
                        onClick={() => dispatch(viewUploadLog({
                            key: info.row.original.key,
                            navigate,
                            path: `/review-upload/${info.row.original.key}`
                        }))}
                    />}
                </>
            ),
            enableHiding: false,
        }),
        columnHelper.accessor('project', {
            header: 'Project',
            cell: (info) => info.getValue(),
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: (info) => capitalizeFirst(info.getValue()),
        }),
        columnHelper.accessor('source_type', {
            header: 'Source Type',
            cell: (info) => {
                const val = info.getValue();
                return typeof val === 'object' && val !== null ? (val.title || val.name || JSON.stringify(val)) : String(val || '');
            },
        }),
        columnHelper.accessor('uploaded_on', {
            header: 'Uploaded On',
            cell: (info) => {
                const val = info.getValue();
                return val instanceof Date ? val.toLocaleString() : String(val || '');
            },
        }),
        columnHelper.accessor('uploaded_by', {
            header: 'Uploaded By',
            cell: (info) => String(info.getValue() || ''),
        })
    ];

    const table = useReactTable({
        data: uploadLogs,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageIndex: UploadLogData?.pagination?.currentPage - 1,
                pageSize: UploadLogData?.pagination?.perPage,
            },
        },
        manualPagination: true,
        pageCount: UploadLogData?.pagination?.totalPages,
    });

    const debouncedGetUploadLogs = useMemo(() => debounce((params) => {
        dispatch(getUploadLogs(params));
    }, 300), [dispatch]);

    useEffect(() => {
        debouncedGetUploadLogs({
            currentPage: 1,
            perPage: UploadLogData?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters
        });
    }, [debouncedGetUploadLogs, filters, UploadLogData?.pagination?.perPage]);

    const loadUploadLogs = () => {
        debouncedGetUploadLogs({
            currentPage: 1,
            perPage: UploadLogData?.pagination?.perPage || constants.tableUI.defaultPerpage,
            ...filters
        });
    };

    const handlePageChange = (currentPage, perPage) => {
        dispatch(getUploadLogs({ currentPage, perPage, ...filters }));
    };

    function handleShowViewLogModal(data) {
        setViewUploadLogData(data);
        setOpenViewLogModal(true);
    }

    const handleHideViewLogModal = () => {
        setOpenViewLogModal(false)
    }

    const toggleShowPending = () => {
        dispatch(setFilters({...filters, pending: filters.pending ? "" : true}))
    }

    function getRefreshButtonClasses(key) {
        return rotatingRows.has(key) ? " [&>svg]:animate-spin [&>svg]:duration-500" : "";
    }

    function handleRefreshClick(key) {
        if (rotatingRows.has(key)) return;

        const newSet = new Set(rotatingRows);
        newSet.add(key);
        setRotatingRows(newSet);

        dispatch(getUploadLogData({ key }));

        setTimeout(() => {
            const updatedSet = new Set(rotatingRows);
            updatedSet.delete(key);
            setRotatingRows(updatedSet);
        }, 500);
    }

    const contextValue = {
        table,
        columns,
        uploadLogs,
        action,
        setAction,
        pagination: UploadLogData?.pagination,
        handlePageChange,
        openDeleteConfirmation,
        openViewLogModal,
        setOpenViewLogModal,
        viewUploadLogData,
        handleShowViewLogModal,
        handleHideViewLogModal,
        toggleShowPending,
        loadUploadLogs,
    };
    
    return (
        <UploadHistoryContext.Provider value={contextValue}>
            {children}
        </UploadHistoryContext.Provider>
    );
};

export const useUploadHistoryContext = () => useContext(UploadHistoryContext);
